import type { IntlShape } from 'react-intl';

import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { formatDaysOfWeek, normalizeDaysOfWeek } from '@src/packages/utils/dates/formatDaysOfWeek';
import { translateString } from '@src/util/intl/translateString';

import { WORK_JOURNEY_STATUS } from './constants';
import type { WorkJourneyFiltersState } from './types';

export const WORK_JOURNEY_LIST_FILTER_FIELDS = {
  SEARCH: 'search',
  USER_SEARCH: 'userSearch',
  SHIFT: 'shift',
  STATE: 'state',
  DATE_RANGE: 'dateRange',
} as const;

export const WORK_JOURNEY_FILTER_DEFAULTS = {
  SHIFT: 'all',
  STATE: 'all',
} as const;

export const WORK_JOURNEY_STATE_FILTER_OPTIONS = [
  WORK_JOURNEY_FILTER_DEFAULTS.STATE,
  WORK_JOURNEY_STATUS.ACTIVE,
  WORK_JOURNEY_STATUS.INACTIVE,
  WORK_JOURNEY_STATUS.PENDING,
  WORK_JOURNEY_STATUS.UPDATED,
] as const;

const getWorkJourneyStateMessageId = (state: string): string => {
  switch (state) {
    case WORK_JOURNEY_STATUS.ACTIVE:
      return 'workJourney.state.active';
    case WORK_JOURNEY_STATUS.INACTIVE:
      return 'workJourney.state.inactive';
    case WORK_JOURNEY_STATUS.PENDING:
      return 'workJourney.state.pending';
    case WORK_JOURNEY_STATUS.UPDATED:
      return 'workJourney.state.updated';
    default:
      return 'workJourney.filters.all';
  }
};

export const getWorkJourneyStateFilterLabel = (state: string, intl: IntlShape): string =>
  intl.formatMessage({ id: getWorkJourneyStateMessageId(state) });

export const INIT_WORK_JOURNEY_LIST_FILTERS: FilterValues = {
  [WORK_JOURNEY_LIST_FILTER_FIELDS.SEARCH]: {
    value: '',
    label: translateString('workJourney.filters.journeyName'),
    valueType: 'string',
  },
  [WORK_JOURNEY_LIST_FILTER_FIELDS.USER_SEARCH]: {
    value: [],
    label: translateString('workJourney.filters.userSearch'),
    valueType: 'array',
    clearValue: [],
  },
  [WORK_JOURNEY_LIST_FILTER_FIELDS.SHIFT]: {
    value: WORK_JOURNEY_FILTER_DEFAULTS.SHIFT,
    label: translateString('workJourney.filters.shifts'),
    valueType: 'string',
    clearValue: WORK_JOURNEY_FILTER_DEFAULTS.SHIFT,
    hideWhenCleared: true,
  },
  [WORK_JOURNEY_LIST_FILTER_FIELDS.STATE]: {
    value: WORK_JOURNEY_STATUS.ACTIVE,
    label: translateString('workJourney.filters.state'),
    valueTranslated: translateString(getWorkJourneyStateMessageId(WORK_JOURNEY_STATUS.ACTIVE)),
    valueType: 'string',
    clearValue: WORK_JOURNEY_FILTER_DEFAULTS.STATE,
    hideWhenCleared: true,
  },
  [WORK_JOURNEY_LIST_FILTER_FIELDS.DATE_RANGE]: {
    value: [],
    label: translateString('workJourney.filters.dateRange'),
    valueType: 'array',
    clearValue: [],
  },
};

export const filterValuesToWorkJourneyFilters = (
  values: FilterValues,
): WorkJourneyFiltersState => {
  const rawDateRange = values[WORK_JOURNEY_LIST_FILTER_FIELDS.DATE_RANGE]?.value;
  const dateRange = Array.isArray(rawDateRange) ? normalizeDaysOfWeek(rawDateRange) : [];

  return {
    search: String(values[WORK_JOURNEY_LIST_FILTER_FIELDS.SEARCH]?.value ?? ''),
    userIds: Array.isArray(values[WORK_JOURNEY_LIST_FILTER_FIELDS.USER_SEARCH]?.value)
      ? (values[WORK_JOURNEY_LIST_FILTER_FIELDS.USER_SEARCH]?.value as unknown[]).filter(
          (id): id is string => typeof id === 'string' && id.trim() !== '',
        )
      : [],
    division: 'all',
    shift: String(
      values[WORK_JOURNEY_LIST_FILTER_FIELDS.SHIFT]?.value ??
        WORK_JOURNEY_FILTER_DEFAULTS.SHIFT,
    ),
    state: String(
      values[WORK_JOURNEY_LIST_FILTER_FIELDS.STATE]?.value ?? WORK_JOURNEY_STATUS.ACTIVE,
    ),
    dateRange,
  };
};

export const formatWorkJourneyDaysForTag = (days: unknown): string => formatDaysOfWeek(days);

export const buildWorkJourneyFilterSetValueProps = (
  key: string,
  value: unknown,
  intl: IntlShape,
  valueTranslated?: string,
): SetValueProps => {
  switch (key) {
    case WORK_JOURNEY_LIST_FILTER_FIELDS.SEARCH:
      return {
        key,
        value: String(value ?? ''),
        label: intl.formatMessage({ id: 'workJourney.filters.journeyName' }),
        valueTranslated: String(value ?? ''),
        valueType: 'string',
      };
    case WORK_JOURNEY_LIST_FILTER_FIELDS.USER_SEARCH:
      return {
        key,
        value: Array.isArray(value)
          ? value.filter((id): id is string => typeof id === 'string' && id.trim() !== '')
          : [],
        label: intl.formatMessage({ id: 'workJourney.filters.userSearch' }),
        valueTranslated: valueTranslated ?? '',
        valueType: 'array',
        clearValue: [],
      };
    case WORK_JOURNEY_LIST_FILTER_FIELDS.SHIFT:
      return {
        key,
        value: String(value ?? WORK_JOURNEY_FILTER_DEFAULTS.SHIFT),
        label: intl.formatMessage({ id: 'workJourney.filters.shifts' }),
        valueTranslated:
          valueTranslated ??
          intl.formatMessage({ id: 'workJourney.filters.all' }),
        valueType: 'string',
        clearValue: WORK_JOURNEY_FILTER_DEFAULTS.SHIFT,
        hideWhenCleared: true,
      };
    case WORK_JOURNEY_LIST_FILTER_FIELDS.STATE: {
      const stateValue = String(value ?? WORK_JOURNEY_STATUS.ACTIVE);
      return {
        key,
        value: stateValue,
        label: intl.formatMessage({ id: 'workJourney.filters.state' }),
        valueTranslated:
          valueTranslated ?? getWorkJourneyStateFilterLabel(stateValue, intl),
        valueType: 'string',
        clearValue: WORK_JOURNEY_FILTER_DEFAULTS.STATE,
        hideWhenCleared: true,
      };
    }
    case WORK_JOURNEY_LIST_FILTER_FIELDS.DATE_RANGE: {
      const days = Array.isArray(value) ? value.map(String) : [];
      return {
        key,
        value: days,
        label: intl.formatMessage({ id: 'workJourney.filters.dateRange' }),
        valueTranslated: formatWorkJourneyDaysForTag(days),
        valueType: 'array',
        clearValue: [],
      };
    }
    default:
      return { key, value: value as SetValueProps['value'], valueType: 'string' };
  }
};
